import React, { useEffect, useState, useCallback, useRef } from 'react';
import { motion, AnimatePresence, MotionConfig, useMotionValue, useTransform, animate } from 'framer-motion';
import { API_URLS } from '../config/api';

/* ============================================================
   CONSTANTES
   Police Outfit, boutons "relief", transform / opacity uniquement.
   ============================================================ */
const EASE = [0.22, 1, 0.36, 1];
const SPRING = { type: 'spring', damping: 30, stiffness: 320 };

const FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;800;900&display=swap');
.spx { font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif; }
.spx button { -webkit-tap-highlight-color: transparent; font-family: inherit; }
.spx .scrollbar-hide::-webkit-scrollbar { display: none; }
.spx .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
`;

const BTN_BLUE = 'bg-[#4A90E2] text-white border-b-4 border-[#2F6FB8] active:border-b-0 active:translate-y-1';
const BTN_NAVY = 'bg-[#2B4360] text-white border-b-4 border-[#1A2B40] active:border-b-0 active:translate-y-1';
const BTN_GREEN = 'bg-[#7C9374] text-white border-b-4 border-[#5D7355] active:border-b-0 active:translate-y-1';
const BTN_WHITE = 'bg-white text-[#1A2B40] border-2 border-b-4 border-slate-200 active:border-b-2 active:translate-y-[2px]';
const BTN_BASE = 'rounded-2xl font-extrabold transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] focus-visible:ring-offset-2';

const cardVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.28, ease: EASE, delay: Math.min(i, 8) * 0.04 } })
};

/* ============================================================
   HELPERS
   ============================================================ */
const readUserInfo = (withLegacy = false) => {
  try {
    const raw = sessionStorage.getItem('user_info')
      || localStorage.getItem('user_info')
      || (withLegacy ? localStorage.getItem('user') : null)
      || '{}';
    return JSON.parse(raw);
  } catch (e) {
    return {};
  }
};
const getUserId = (u) => u.id || (u.user && u.user.id);

const buzz = (ms = 12) => {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch (e) { /* noop */ }
};

const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

const useBodyLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [active]);
};

const useEscape = (active, handler) => {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handler(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, handler]);
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/* ============================================================
   MATHS
   ============================================================ */
const formatMathExpression = (text) => {
  if (!text) return text;
  let formatted = text;
  formatted = formatted.replace(/(\d+)\/(\d+)/g, (match, num, den) => `\\frac{${num}}{${den}}`);
  formatted = formatted.replace(/\(([^)]+)\)\/\(([^)]+)\)/g, (match, num, den) => `\\frac{${num}}{${den}}`);
  formatted = formatted.replace(/([a-zA-Z]+)\s*=\s*([^=]+)/g, (match, variable, expression) => {
    let expr = expression.replace(/×/g, ' \\times ').replace(/÷/g, ' \\div ').replace(/\+/g, ' + ').replace(/(?<!\s)-(?!\s*\d)/g, ' - ');
    return `${variable} = ${expr}`;
  });
  return formatted.replace(/\s+/g, ' ').trim();
};

const MathExpression = React.memo(({ children, className = '' }) => {
  const formattedText = formatMathExpression(children);
  return (
    <div className="overflow-x-auto max-w-full pb-1 scrollbar-hide">
      <span
        className={`math-expression font-mono text-slate-800 inline-block ${className}`}
        dangerouslySetInnerHTML={{
          __html: (formattedText || '')
            .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="inline-flex flex-col text-center align-middle mx-1"><span class="border-b border-slate-300 px-1 text-xs leading-tight">$1</span><span class="px-1 text-xs leading-tight">$2</span></span>')
            .replace(/\\times/g, '×')
            .replace(/\\div/g, '÷')
        }}
      />
    </div>
  );
});

/* ============================================================
   MOTIFS CSS
   ============================================================ */
const Corners = ({ size = 'w-3 h-3', border = 'border-white', thick = 'border-2' }) => (
  <>
    <span className={`absolute top-0 left-0 ${size} ${thick} ${border} border-r-0 border-b-0 rounded-tl-md`} />
    <span className={`absolute top-0 right-0 ${size} ${thick} ${border} border-l-0 border-b-0 rounded-tr-md`} />
    <span className={`absolute bottom-0 left-0 ${size} ${thick} ${border} border-r-0 border-t-0 rounded-bl-md`} />
    <span className={`absolute bottom-0 right-0 ${size} ${thick} ${border} border-l-0 border-t-0 rounded-br-md`} />
  </>
);

// Balayage lumineux : la copie est « lue »
const ScanSweep = ({ loop }) => (
  <motion.span
    key={loop ? 'loop' : 'once'}
    className="pointer-events-none absolute inset-0"
    style={{ background: 'linear-gradient(to bottom, transparent 42%, rgba(74,144,226,0.5) 50%, transparent 58%)' }}
    initial={{ y: '-100%' }} animate={{ y: '100%' }}
    transition={loop ? { duration: 1.6, repeat: Infinity, ease: 'easeInOut' } : { duration: 1.1, ease: EASE, delay: 0.25 }}
    aria-hidden="true"
  />
);

/* ============================================================
   VISIONNEUSE D'IMAGE : pincer, glisser, double-tap, molette
   - Les valeurs vivent dans des MotionValues : aucun re-rendu React pendant le geste.
   - Image à l'échelle 1 : glisser vers le haut ou le bas la ferme.
   ============================================================ */
const MAX_ZOOM = 5;
const DOUBLE_TAP_ZOOM = 2.5;

const ImageViewer = ({ src, alt, onClose }) => {
  useBodyLock(true);
  useEscape(true, onClose);

  const boxRef = useRef(null);
  const imgRef = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const scale = useMotionValue(1);
  const backdrop = useTransform(y, [-320, 0, 320], [0.35, 1, 0.35]);

  const pointers = useRef(new Map());
  const pinch = useRef({ dist: 0, cx: 0, cy: 0 });
  const gesture = useRef({ moved: 0, lastTap: 0 });

  // Dimensions réellement affichées de l'image (object-contain) : sert à borner le déplacement
  const bounds = (s) => {
    const box = boxRef.current;
    const img = imgRef.current;
    if (!box || !img) return { bx: 0, by: 0 };
    const cw = box.clientWidth;
    const ch = box.clientHeight;
    const fit = Math.min(cw / (img.naturalWidth || 1), ch / (img.naturalHeight || 1));
    const dw = (img.naturalWidth || cw) * fit;
    const dh = (img.naturalHeight || ch) * fit;
    return { bx: Math.max(0, (dw * s - cw) / 2), by: Math.max(0, (dh * s - ch) / 2) };
  };

  // Coordonnées relatives au centre du conteneur
  const rel = (cx, cy) => {
    const r = boxRef.current.getBoundingClientRect();
    return { px: cx - r.left - r.width / 2, py: cy - r.top - r.height / 2 };
  };

  const settle = () => {
    const s = scale.get();
    if (s <= 1.02) {
      if (Math.abs(y.get()) > 110) { onClose(); return; }
      animate(scale, 1, SPRING); animate(x, 0, SPRING); animate(y, 0, SPRING);
      return;
    }
    const { bx, by } = bounds(s);
    animate(x, clamp(x.get(), -bx, bx), SPRING);
    animate(y, clamp(y.get(), -by, by), SPRING);
  };

  const zoomTo = (target, px, py) => {
    const ratio = target / scale.get();
    const { bx, by } = bounds(target);
    animate(scale, target, SPRING);
    animate(x, clamp(px - (px - x.get()) * ratio, -bx, bx), SPRING);
    animate(y, clamp(py - (py - y.get()) * ratio, -by, by), SPRING);
  };

  const onPointerDown = (e) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current.moved = 0;
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      const c = rel((a.x + b.x) / 2, (a.y + b.y) / 2);
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, cx: c.px, cy: c.py };
    }
  };

  const onPointerMove = (e) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    const dx = e.clientX - prev.x;
    const dy = e.clientY - prev.y;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesture.current.moved += Math.abs(dx) + Math.abs(dy);

    if (pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const c = rel((a.x + b.x) / 2, (a.y + b.y) / 2);
      const s0 = scale.get();
      const s1 = clamp(s0 * (dist / pinch.current.dist), 1, MAX_ZOOM);
      const eff = s1 / s0;
      // Le point sous les doigts reste fixe, et le centre du pincement entraîne l'image
      x.set(c.px - (pinch.current.cx - x.get()) * eff);
      y.set(c.py - (pinch.current.cy - y.get()) * eff);
      scale.set(s1);
      pinch.current = { dist, cx: c.px, cy: c.py };
      return;
    }

    const s = scale.get();
    if (s <= 1.02) {
      y.set(y.get() + dy); // glisser pour fermer
    } else {
      const { bx, by } = bounds(s);
      // Résistance élastique au-delà des bords
      x.set(x.get() + dx * (Math.abs(x.get()) > bx ? 0.35 : 1));
      y.set(y.get() + dy * (Math.abs(y.get()) > by ? 0.35 : 1));
    }
  };

  const onPointerUp = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size > 0) return;

    if (gesture.current.moved < 8) {
      const now = Date.now();
      if (now - gesture.current.lastTap < 280) {
        gesture.current.lastTap = 0;
        const { px, py } = rel(e.clientX, e.clientY);
        zoomTo(scale.get() > 1.02 ? 1 : DOUBLE_TAP_ZOOM, px, py);
        return;
      }
      gesture.current.lastTap = now;
    }
    settle();
  };

  const onWheel = (e) => {
    const s0 = scale.get();
    const s1 = clamp(s0 * Math.exp(-e.deltaY * 0.0025), 1, MAX_ZOOM);
    const eff = s1 / s0;
    const { px, py } = rel(e.clientX, e.clientY);
    const { bx, by } = bounds(s1);
    x.set(clamp(px - (px - x.get()) * eff, -bx, bx));
    y.set(clamp(py - (py - y.get()) * eff, -by, by));
    scale.set(s1);
  };

  return (
    <motion.div
      role="dialog" aria-modal="true" aria-label="Énoncé en plein écran"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[90] flex flex-col"
    >
      <motion.div className="absolute inset-0 bg-[#0E1824]" style={{ opacity: backdrop }} aria-hidden="true" />

      <div className="relative z-10 flex items-center justify-between px-4 pb-3 pointer-events-none" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
        <button
          type="button"
          onClick={onClose}
          className="pointer-events-auto min-h-[44px] px-5 rounded-full bg-white/15 text-white text-sm font-extrabold active:bg-white/25 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          Fermer
        </button>
        <span className="text-xs font-bold text-white/70">Pince ou double-tape pour zoomer</span>
      </div>

      <div
        ref={boxRef}
        className="relative flex-1 overflow-hidden select-none cursor-zoom-in"
        style={{ touchAction: 'none' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
        onDoubleClick={(e) => { const { px, py } = rel(e.clientX, e.clientY); if (e.pointerType !== 'touch') zoomTo(scale.get() > 1.02 ? 1 : DOUBLE_TAP_ZOOM, px, py); }}
      >
        <motion.img
          ref={imgRef}
          src={src}
          alt={alt}
          draggable={false}
          decoding="async"
          style={{ x, y, scale }}
          className="absolute inset-0 w-full h-full object-contain will-change-transform"
        />
      </div>
    </motion.div>
  );
};

// Énoncé sous forme d'image : visible d'un coup, on touche pour l'ouvrir et zoomer
const StatementImage = ({ src, onFail }) => {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const closeViewer = useCallback(() => setOpen(false), []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Agrandir l'énoncé"
        className="relative block w-full rounded-3xl border-2 border-slate-200 bg-white overflow-hidden active:scale-[0.995] transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] focus-visible:ring-offset-2"
      >
        {!loaded && <span className="block min-h-[220px] bg-slate-100 animate-pulse" aria-hidden="true" />}
        <img
          src={src}
          alt="Énoncé de l'exercice"
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={onFail}
          className={`w-full h-auto ${loaded ? 'block opacity-100' : 'hidden opacity-0'}`}
        />
        {loaded && (
          <span className="absolute bottom-3 right-3 px-3.5 py-2 rounded-full bg-[#1A2B40]/90 text-white text-xs font-extrabold">
            Toucher pour zoomer
          </span>
        )}
      </button>
      <AnimatePresence>
        {open && <ImageViewer key="viewer" src={src} alt="Énoncé de l'exercice" onClose={closeViewer} />}
      </AnimatePresence>
    </>
  );
};

/* ============================================================
   CHRONO D'EXAMEN (isolé : le parent ne se re-rend pas chaque seconde)
   ============================================================ */
const ExamTimer = ({ startTime, stopped }) => {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!startTime) { setSecs(0); return undefined; }
    const tick = () => setSecs(Math.floor((Date.now() - startTime) / 1000));
    tick();
    if (stopped) return undefined;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startTime, stopped]);

  return (
    <div className="flex items-center gap-2.5 px-4 min-h-[44px] bg-white rounded-full border-2 border-slate-200" role="timer" aria-label="Temps écoulé">
      <span className={`w-2 h-2 rounded-full bg-[#4A90E2] ${stopped ? '' : 'motion-safe:animate-pulse'}`} aria-hidden="true" />
      <span className="font-black text-[#1A2B40] text-sm tabular-nums">{formatTime(secs)}</span>
    </div>
  );
};

/* ============================================================
   CAMÉRA
   ============================================================ */
const CustomCamera = ({ onCapture, onClose, onUseGallery }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const streamRef = useRef(null);
  const [error, setError] = useState(null);
  const [capturing, setCapturing] = useState(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
        });
        if (cancelled) { mediaStream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = mediaStream;
        if (videoRef.current) videoRef.current.srcObject = mediaStream;
      } catch (err) {
        console.error('Erreur caméra:', err);
        if (!cancelled) setError("Impossible d'accéder à la caméra. Vérifie l'autorisation dans ton navigateur, ou choisis une image dans ta galerie.");
      }
    })();
    return () => { cancelled = true; stopCamera(); };
  }, [stopCamera]);

  const takePhoto = () => {
    if (capturing) return;
    if (videoRef.current && canvasRef.current && containerRef.current && overlayRef.current) {
      setCapturing(true);
      buzz(25);
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const container = containerRef.current;
      const overlay = overlayRef.current;

      const videoWidth = video.videoWidth;
      const videoHeight = video.videoHeight;
      const containerRect = container.getBoundingClientRect();
      const overlayRect = overlay.getBoundingClientRect();

      const videoRatio = videoWidth / videoHeight;
      const containerRatio = containerRect.width / containerRect.height;

      let renderWidth, renderHeight, offsetX, offsetY;
      if (containerRatio > videoRatio) {
        renderWidth = containerRect.width;
        renderHeight = containerRect.width / videoRatio;
        offsetX = 0;
        offsetY = (renderHeight - containerRect.height) / 2;
      } else {
        renderWidth = containerRect.height * videoRatio;
        renderHeight = containerRect.height;
        offsetX = (renderWidth - containerRect.width) / 2;
        offsetY = 0;
      }

      const cropX_visual = (overlayRect.left - containerRect.left) + offsetX;
      const cropY_visual = (overlayRect.top - containerRect.top) + offsetY;
      const scale = videoWidth / renderWidth;

      const sourceX = cropX_visual * scale;
      const sourceY = cropY_visual * scale;
      const sourceWidth = overlayRect.width * scale;
      const sourceHeight = overlayRect.height * scale;

      canvas.width = sourceWidth;
      canvas.height = sourceHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);

      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], 'exercice_crop.jpg', { type: 'image/jpeg' });
          onCapture(file);
          stopCamera();
        } else {
          setCapturing(false);
        }
      }, 'image/jpeg', 0.95);
    }
  };

  return (
    <motion.div
      role="dialog" aria-modal="true" aria-label="Scanner la copie"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="spx fixed inset-0 z-[100] bg-black flex flex-col"
    >
      <div className="absolute top-0 w-full px-4 pb-4 flex justify-between items-center z-20 bg-gradient-to-b from-black/80 to-transparent" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
        <button
          type="button"
          onClick={() => { stopCamera(); onClose(); }}
          className="min-h-[44px] px-5 text-white text-sm font-extrabold bg-black/40 rounded-full active:scale-95 transition-transform touch-manipulation"
        >
          Fermer
        </button>
        <span className="text-white/90 font-bold text-sm bg-black/40 px-4 py-2 rounded-full">Scanner la copie</span>
        <div className="w-[84px]" />
      </div>

      {error ? (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-white text-center p-6 bg-white/5 border border-white/15 rounded-3xl max-w-sm" role="alert">
            <p className="mb-5 font-medium leading-relaxed">{error}</p>
            <button type="button" onClick={onUseGallery} className={`${BTN_WHITE} w-full min-h-[52px] px-6 ${BTN_BASE}`}>
              Ouvrir la galerie
            </button>
            <button type="button" onClick={onClose} className="w-full min-h-[48px] mt-2 text-white/60 font-bold touch-manipulation">
              Fermer
            </button>
          </div>
        </div>
      ) : (
        <div ref={containerRef} className="relative flex-1 w-full flex items-center justify-center overflow-hidden bg-black">
          <video ref={videoRef} autoPlay playsInline muted className="absolute inset-0 w-full h-full object-cover" />

          <div ref={overlayRef} className="relative z-10 w-[85%] max-w-md aspect-[1/1.41] rounded-xl shadow-[0_0_0_100vmax_rgba(0,0,0,0.7)] border-2 border-white/40">
            <div className="absolute inset-0 -m-[2px]"><Corners size="w-8 h-8" thick="border-4" /></div>
          </div>

          {capturing && (
            <motion.div
              initial={{ opacity: 0.85 }} animate={{ opacity: 0 }} transition={{ duration: 0.35 }}
              className="absolute inset-0 z-30 bg-white pointer-events-none" aria-hidden="true"
            />
          )}

          <div className="absolute bottom-0 w-full pt-16 flex flex-col justify-center items-center bg-gradient-to-t from-black via-black/50 to-transparent z-20" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' }}>
            <p className="text-white/80 text-sm font-medium mb-4">Cadre ta feuille entière dans le rectangle</p>
            <div className="grid grid-cols-3 items-center w-full max-w-sm px-6 mb-6">
              <button type="button" onClick={onUseGallery} className="justify-self-start min-h-[44px] text-white/80 text-sm font-extrabold active:text-white touch-manipulation">
                Galerie
              </button>
              <button
                type="button"
                onClick={takePhoto}
                disabled={capturing}
                aria-label="Prendre la photo"
                className="relative justify-self-center w-20 h-20 flex items-center justify-center active:scale-90 transition-transform touch-manipulation disabled:opacity-60"
              >
                <div className="absolute inset-0 rounded-full border-[3px] border-white/60" />
                <div className="w-[68px] h-[68px] rounded-full bg-white" />
              </button>
              <span />
            </div>
          </div>
        </div>
      )}
      <canvas ref={canvasRef} className="hidden" />
    </motion.div>
  );
};

/* ============================================================
   PANNEAU « RENDRE MA COPIE » : plein écran, deux étapes, fondu
   ============================================================ */
const PremiumTag = () => (
  <span className="shrink-0 text-[11px] font-extrabold rounded-full px-2.5 py-1 bg-[#2B4360]/10 text-[#2B4360]">Premium</span>
);

const UploadPanel = ({
  uploadedImage, isSubmitting, isExerciseCompleted, fileError, locked,
  onClose, onCamera, onGallery, onRemove, onSubmit
}) => {
  useBodyLock(true);
  useEscape(true, onClose);

  return (
    <motion.div
      role="dialog" aria-modal="true" aria-label="Rendre ma copie"
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 12 }}
      transition={{ duration: 0.26, ease: EASE }}
      className="fixed inset-0 z-[70] bg-[#F4F7FB] flex flex-col"
    >
      <div className="shrink-0 max-w-xl w-full mx-auto px-5" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
        <div className="flex items-center justify-between min-h-[44px]">
          <p className="text-sm font-bold text-slate-500">Étape {uploadedImage ? 2 : 1} sur 2</p>
          <button type="button" onClick={onClose} className="min-h-[44px] -mr-2 px-3 text-sm font-extrabold text-slate-500 active:text-slate-800 touch-manipulation">
            Fermer
          </button>
        </div>
        <div className="flex gap-2 mt-1" aria-hidden="true">
          <span className="h-2 flex-1 rounded-full bg-[#4A90E2]" />
          <span className="relative h-2 flex-1 rounded-full bg-slate-200 overflow-hidden">
            <motion.span
              className="absolute inset-0 origin-left bg-[#4A90E2]"
              initial={false} animate={{ scaleX: uploadedImage ? 1 : 0 }}
              transition={{ duration: 0.45, ease: EASE }}
            />
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}>
        <div className="max-w-xl mx-auto px-5 pt-8">
          <AnimatePresence mode="wait" initial={false}>
            {!uploadedImage ? (
              <motion.div key="choose" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.22, ease: EASE }}>
                <h2 className="text-[32px] font-black text-[#1A2B40] leading-[1.1] tracking-tight">Photographie ta copie</h2>
                <p className="text-[15px] font-medium text-slate-500 mt-2 mb-7 leading-relaxed">
                  Cadre la feuille entière : feuille à plat, bien éclairée, écriture lisible.
                </p>

                <button
                  type="button"
                  onClick={() => { buzz(); onCamera(); }}
                  className={`${BTN_BLUE} ${BTN_BASE} w-full min-h-[64px] px-5 text-lg flex items-center justify-center gap-3`}
                >
                  Prendre en photo {locked && <PremiumTag />}
                </button>
                <button
                  type="button"
                  onClick={() => { buzz(); onGallery(); }}
                  className={`${BTN_WHITE} ${BTN_BASE} mt-3 w-full min-h-[60px] px-5 text-base flex items-center justify-center gap-3`}
                >
                  Choisir dans la galerie {locked && <PremiumTag />}
                </button>
                <p className="mt-3 text-center text-xs font-medium text-slate-400">JPEG ou PNG, 15 Mo maximum</p>

                {fileError && (
                  <p role="alert" className="mt-5 text-sm font-bold text-red-600 bg-red-50 border-2 border-red-100 rounded-2xl px-4 py-3">
                    {fileError}
                  </p>
                )}
              </motion.div>
            ) : (
              <motion.div key="preview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.22, ease: EASE }}>
                <h2 className="text-[32px] font-black text-[#1A2B40] leading-[1.1] tracking-tight">
                  {isSubmitting ? 'Lecture de ta copie' : 'Ta copie est prête'}
                </h2>
                <p className="text-[15px] font-medium text-slate-500 mt-2 mb-5 leading-relaxed">
                  {isSubmitting ? 'Le professeur déchiffre ton écriture, un instant.' : 'Vérifie que ton écriture est bien lisible.'}
                </p>

                <div className="relative rounded-3xl overflow-hidden bg-slate-100 border-2 border-slate-200 w-full aspect-[4/3] max-h-[46dvh]">
                  <motion.img
                    src={uploadedImage} alt="Aperçu de ta copie" decoding="async"
                    initial={{ scale: 1.04, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    className="w-full h-full object-contain"
                  />
                  <ScanSweep loop={isSubmitting} />
                  {isSubmitting && <span className="absolute inset-0 bg-[#2B4360]/30 pointer-events-none" />}
                </div>

                <div className="flex gap-3 mt-5">
                  <button
                    type="button"
                    onClick={onRemove}
                    disabled={isSubmitting}
                    className={`${BTN_WHITE} ${BTN_BASE} min-h-[60px] px-6 text-sm disabled:opacity-40`}
                  >
                    Reprendre
                  </button>
                  <button
                    type="button"
                    onClick={() => { buzz(20); onSubmit(); }}
                    disabled={isSubmitting || isExerciseCompleted}
                    className={`${BTN_BASE} flex-1 min-h-[60px] text-base ${
                      isSubmitting || isExerciseCompleted ? 'bg-slate-200 text-slate-400 border-b-4 border-slate-300' : BTN_GREEN
                    }`}
                  >
                    {isSubmitting ? 'Analyse en cours' : 'Valider ma réponse'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

/* ============================================================
   MODALE PREMIUM
   ============================================================ */
const PremiumModal = ({ onClose, onCheckout, isRedirecting }) => {
  useBodyLock(true);
  useEscape(true, onClose);

  return (
    <motion.div
      role="dialog" aria-modal="true" aria-label="Passer en Premium"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
      className="fixed inset-0 bg-[#1A2B40]/90 z-[100] flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
        transition={{ duration: 0.26, ease: EASE }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[400px] rounded-[32px] bg-white p-7"
        style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
      >
        <h3 className="text-[30px] font-black text-[#1A2B40] tracking-tight leading-[1.1] mb-3">
          Débloque ton tuteur mathématique
        </h3>
        <p className="text-slate-500 text-[15px] font-medium leading-relaxed mb-6">
          Tu as utilisé ton essai gratuit. Passe en Premium pour continuer sans limite.
        </p>

        <ul className="space-y-3 mb-7">
          {['Corrections détaillées, étape par étape', 'Explications vocales avec le professeur', 'Exercices en illimité'].map((line) => (
            <li key={line} className="flex items-center gap-3 text-[15px] font-bold text-[#1A2B40]">
              <span className="w-1.5 h-6 rounded-full bg-[#4A90E2] shrink-0" aria-hidden="true" />
              {line}
            </li>
          ))}
        </ul>

        <button
          type="button"
          onClick={() => { buzz(20); onCheckout(); }}
          disabled={isRedirecting}
          className={`${BTN_BLUE} ${BTN_BASE} w-full min-h-[58px] text-base disabled:opacity-60`}
        >
          {isRedirecting ? 'Redirection en cours' : 'Passer en Premium'}
        </button>
        <button type="button" onClick={onClose} className="w-full min-h-[48px] mt-2 text-slate-500 text-sm font-bold active:text-slate-800 touch-manipulation">
          Peut-être plus tard
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
const SmartExContentInner = ({
  chapter, onBack, onAnalysisComplete, examMode = false,
  currentExercise = 0, totalExercises = 1, onExerciseComplete = null,
  errorMessage = null, onDismissError = null
}) => {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [showCustomCamera, setShowCustomCamera] = useState(false);
  const [statementImageFailed, setStatementImageFailed] = useState(false);

  const [examStartTime, setExamStartTime] = useState(null);
  const [isExerciseCompleted, setIsExerciseCompleted] = useState(false);

  // --- ABONNEMENT ET USAGE ---
  const [userPlan, setUserPlan] = useState('Gratuit');
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const [hasUsedFreeTrial, setHasUsedFreeTrial] = useState(false);
  const [isManaging, setIsManaging] = useState(false);

  const fileInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  // --- APERÇU : URL objet (au lieu d'un base64 de plusieurs Mo en state) ---
  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setUploadedImage(null);
    setUploadedFile(null);
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  // --- VÉRIFICATION DE L'USAGE (BDD) ---
  const checkUsage = useCallback(async () => {
    const userInfo = readUserInfo();
    const userId = getUserId(userInfo);

    const plan = userInfo?.plan || userInfo?.user?.plan || 'Gratuit';
    setUserPlan(plan);

    if (userId && !examMode) {
      try {
        const backendUrl = API_URLS.BASE_URL || 'http://localhost:8000';
        const res = await fetch(`${backendUrl}/api/check-usage/${userId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const persistPlan = (newPlan) => {
          const updatedUser = { ...userInfo, plan: newPlan };
          if (sessionStorage.getItem('user_info')) sessionStorage.setItem('user_info', JSON.stringify(updatedUser));
          if (localStorage.getItem('user_info')) localStorage.setItem('user_info', JSON.stringify(updatedUser));
        };

        if (data.plan === 'Premium' || data.plan === 'premium') {
          setUserPlan('Premium');
          persistPlan('Premium');
          setHasUsedFreeTrial(false);
        } else {
          setUserPlan('Gratuit');
          persistPlan('Gratuit');
          // Source de vérité = BDD (le token n'est créé qu'après une analyse réussie)
          setHasUsedFreeTrial(!!data.has_used);
        }
      } catch (e) {
        console.error('Erreur vérification usage:', e);
      }
    }
  }, [examMode]);

  useEffect(() => { checkUsage(); }, [checkUsage]);

  // --- RÉINITIALISATION APRÈS UNE ERREUR D'ANALYSE ---
  useEffect(() => {
    if (errorMessage) {
      setIsSubmitting(false);
      setIsExerciseCompleted(false);
      clearPreview();
      setIsWidgetOpen(false);
      // L'essai gratuit n'est consommé en BDD que si l'analyse réussit : on resynchronise
      checkUsage();
    }
  }, [errorMessage, checkUsage, clearPreview]);

  // --- INTERCEPTEUR POUR BLOQUER LE 2EME ESSAI ---
  const handleInteraction = (actionCallback) => {
    if (userPlan === 'Premium') { actionCallback(); return; }
    if (hasUsedFreeTrial && !examMode) {
      setIsWidgetOpen(false);
      setShowPremiumModal(true);
    } else {
      actionCallback();
    }
  };
  const isLocked = userPlan !== 'Premium' && hasUsedFreeTrial && !examMode;
  const isPremium = userPlan === 'Premium' && !examMode;

  // --- CHECKOUT STRIPE ---
  const handleCheckout = async () => {
    setIsRedirectingToStripe(true);
    try {
      const userId = getUserId(readUserInfo());
      const backendUrl = API_URLS.BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Erreur lors de l'initialisation du paiement.");
        setIsRedirectingToStripe(false);
      }
    } catch (error) {
      console.error('Erreur Checkout:', error);
      setIsRedirectingToStripe(false);
    }
  };

  // --- PORTAIL CLIENT STRIPE ---
  const handleManageSubscription = async () => {
    if (isManaging) return;
    setIsManaging(true);
    try {
      const userId = getUserId(readUserInfo());
      const backendUrl = API_URLS.BASE_URL || 'http://localhost:8000';
      const response = await fetch(`${backendUrl}/api/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert("Impossible d'ouvrir l'espace de gestion Stripe.");
      }
    } catch (error) {
      console.error('Erreur Portal:', error);
    } finally {
      setIsManaging(false);
    }
  };

  // --- MODE EXAMEN : réinitialisation à chaque exercice ---
  useEffect(() => {
    if (examMode) {
      setIsSubmitting(false);
      setIsExerciseCompleted(false);
      clearPreview();
      setFileError(null);
      setIsWidgetOpen(true);
      setExamStartTime(Date.now());
    }
  }, [examMode, currentExercise, clearPreview]);

  useEffect(() => {
    if (examMode && !examStartTime) setExamStartTime(Date.now());
  }, [examMode, examStartTime]);

  const getElapsedSeconds = () => (examStartTime ? Math.floor((Date.now() - examStartTime) / 1000) : 0);

  // --- FICHIERS ---
  const handleFile = (file) => {
    setFileError(null);
    if (file && (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg')) {
      if (file.size > 15 * 1024 * 1024) {
        setFileError('Fichier trop volumineux (15 Mo maximum).');
        setIsWidgetOpen(true);
        return;
      }
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const url = URL.createObjectURL(file);
      previewUrlRef.current = url;
      setUploadedImage(url);
      setUploadedFile(file);
      setIsWidgetOpen(true);
    } else {
      setFileError('Format accepté : JPEG ou PNG.');
      setIsWidgetOpen(true);
    }
  };

  const handleDragEnter = useCallback((e) => { e.preventDefault(); e.stopPropagation(); if (!isSubmitting) { setIsDragging(true); setIsWidgetOpen(true); } }, [isSubmitting]);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget)) return; setIsDragging(false); }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); e.stopPropagation(); }, []);
  const handleDrop = useCallback((e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    if (isSubmitting) return;
    const files = e.dataTransfer.files;
    if (files && files[0]) handleFile(files[0]);
  }, [isSubmitting]);

  const handleFileInput = (e) => {
    if (isSubmitting) return;
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = ''; // permet de re-choisir le même fichier
  };

  const removeImage = (e) => { e?.stopPropagation(); if (!isSubmitting) { clearPreview(); setFileError(null); } };

  const handleSubmit = async () => {
    if (!uploadedFile || isSubmitting) return;
    setIsSubmitting(true);

    const userInfo = readUserInfo(true);
    const userId = getUserId(userInfo);
    const studentName = userInfo.firstName || userInfo.first_name || (userInfo.user && userInfo.user.first_name) || 'Étudiant';

    if (!userId) { alert('Erreur : utilisateur non connecté (ID introuvable).'); setIsSubmitting(false); return; }

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      formData.append('user_id', userId);
      const exerciseId = (chapter && chapter.id) ? String(chapter.id) : '';
      formData.append('exercise_id', exerciseId);

      const elapsed = getElapsedSeconds();
      if (examMode) {
        formData.append('exam_mode', 'true');
        formData.append('exercise_time', elapsed.toString());
        formData.append('exercise_index', currentExercise.toString());
      }

      const requestOptions = { method: 'POST', body: formData, headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache', 'Expires': '0' } };

      if (!examMode) setHasUsedFreeTrial(true);

      if (examMode) {
        if (onExerciseComplete) {
          onExerciseComplete({
            exerciseIndex: currentExercise,
            result: { status: 'analysis_in_progress', message: 'Analyse en cours...', exercise_id: exerciseId },
            completionTime: elapsed,
            imageUrl: URL.createObjectURL(uploadedFile)
          });
        }
        fetch(API_URLS.ANALYZE_PROGRESSIVE, requestOptions).catch(console.error);
      } else {
        setIsExerciseCompleted(true);
        if (onAnalysisComplete) {
          onAnalysisComplete({ status: 'analysis_in_progress', message: 'Analyse démarrée...', exercise_id: exerciseId, student_name: studentName, timestamp: Date.now() }, true, studentName);
        }
        // Les erreurs serveur sont remontées via analysis_progress.json (status "error")
        // et gérées par Loading.jsx → onError → retour ici avec errorMessage.
        fetch(API_URLS.ANALYZE_PROGRESSIVE, requestOptions).catch((err) => console.error('Erreur envoi:', err));
      }
    } catch (error) {
      console.error(error);
      setIsSubmitting(false);
      alert("Erreur lors de l'envoi.");
    }
  };

  const closeWidget = useCallback(() => setIsWidgetOpen(false), []);
  const closePremium = useCallback(() => setShowPremiumModal(false), []);
  const onStatementImageFail = useCallback(() => setStatementImageFailed(true), []);

  /* ---------- Contenu de l'énoncé ----------
     image_statement renseignée : on n'affiche QUE l'image (zoomable).
     Sinon (ou si l'image ne charge pas) : le texte du champ statement, comme avant. */
  const statementImageUrl = typeof chapter?.image_statement === 'string' ? chapter.image_statement.trim() : '';
  const showImageStatement = !!statementImageUrl && !statementImageFailed;

  const statement = chapter?.statement || {};
  const enonce = statement.énoncé || statement.enonce || '';
  const questions = Object.entries(statement).filter(([key]) => key.startsWith('Q') && key !== 'énoncé' && key !== 'enonce');

  const widgetsVisible = !isWidgetOpen && !showPremiumModal && !showCustomCamera;
  const ctaLabel = isExerciseCompleted ? 'Copie envoyée' : uploadedImage ? 'Envoyer ma copie' : errorMessage ? 'Réessayer' : 'Photographier mes solutions';
  const ctaClass = isExerciseCompleted ? 'bg-slate-200 text-slate-400 border-b-4 border-slate-300' : uploadedImage ? BTN_GREEN : BTN_NAVY;

  return (
    <div className="spx min-h-[100dvh] bg-[#F4F7FB] flex flex-col text-slate-900 selection:bg-[#4A90E2]/20 selection:text-[#2B4360]" onDragEnter={handleDragEnter}>
      <style dangerouslySetInnerHTML={{ __html: FONT_CSS }} />

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleFileInput} className="hidden" tabIndex={-1} aria-hidden="true" />

      <AnimatePresence>
        {showCustomCamera && (
          <CustomCamera
            key="camera"
            onCapture={(file) => { handleFile(file); setShowCustomCamera(false); }}
            onClose={() => setShowCustomCamera(false)}
            onUseGallery={() => { setShowCustomCamera(false); fileInputRef.current?.click(); }}
          />
        )}
      </AnimatePresence>

      {/* ---- En-tête ---- */}
      <header className="bg-[#F4F7FB] sticky top-0 z-30 shrink-0" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className={`${BTN_WHITE} ${BTN_BASE} min-h-[44px] px-4 text-sm shrink-0`}
              >
                Retour
              </button>
            )}
            {examMode && (
              <span className="px-3 py-1.5 rounded-full bg-[#7C9374] text-white text-sm font-extrabold whitespace-nowrap">
                Question {currentExercise + 1}/{totalExercises}
              </span>
            )}
          </div>

          <div className="flex items-center shrink-0">
            {examMode ? (
              <ExamTimer startTime={examStartTime} stopped={isExerciseCompleted} />
            ) : isPremium ? (
              <button
                type="button"
                onClick={handleManageSubscription}
                disabled={isManaging}
                className="min-h-[44px] px-4 rounded-full border-2 border-slate-200 bg-white text-sm font-extrabold text-[#2B4360] active:bg-slate-50 touch-manipulation disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2]"
              >
                {isManaging ? 'Ouverture' : 'Mon abonnement'}
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {/* ---- Bannière d'erreur d'analyse ---- */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="w-full max-w-3xl mx-auto mt-2 px-4 sm:px-6 z-20"
            role="alert"
          >
            <div className="flex items-start gap-3 bg-red-50 border-2 border-red-200 text-red-700 rounded-2xl p-4 text-sm font-bold">
              <span className="flex-1">{errorMessage}</span>
              {onDismissError && (
                <button type="button" onClick={onDismissError} className="shrink-0 min-h-[32px] -my-1 -mr-1 px-2 text-xs font-extrabold active:scale-95 transition-transform touch-manipulation">
                  Fermer
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- Énoncé ---- */}
      <main
        className="flex-1 w-full"
        onDragOver={handleDragOver} onDrop={handleDrop} onDragLeave={handleDragLeave}
        style={{ paddingBottom: 'calc(max(env(safe-area-inset-bottom), 1rem) + 110px)' }}
      >
        <AnimatePresence>
          {isDragging && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="fixed inset-0 bg-white/95 z-[60] flex items-center justify-center pointer-events-none"
            >
              <div className="relative px-12 py-14 flex flex-col items-center">
                <div className="absolute inset-3"><Corners size="w-8 h-8" border="border-[#4A90E2]" thick="border-4" /></div>
                <h3 className="text-3xl font-black text-[#1A2B40]">Dépose ta copie ici</h3>
                <p className="text-sm font-medium text-slate-500 mt-1">JPEG ou PNG</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-6">
          {!examMode && (
            <motion.div custom={0} initial="hidden" animate="visible" variants={cardVariants} className="mb-6">
              <h1 className="text-[34px] sm:text-4xl font-black text-[#1A2B40] tracking-tight leading-[1.08]">
                {chapter?.title || chapter?.chapter}
              </h1>
              {chapter?.title && chapter?.chapter && (
                <p className="mt-1.5 text-[15px] font-bold text-[#4A90E2]">{chapter.chapter}</p>
              )}
            </motion.div>
          )}

          {showImageStatement ? (
            <motion.div custom={1} initial="hidden" animate="visible" variants={cardVariants}>
              <StatementImage src={statementImageUrl} onFail={onStatementImageFail} />
            </motion.div>
          ) : (
            <>
              {enonce && (
                <motion.div custom={1} initial="hidden" animate="visible" variants={cardVariants} className="bg-white rounded-3xl border-2 border-slate-200 mb-4 p-5 sm:p-6 text-base text-slate-700 leading-relaxed">
                  <p className="mb-3 text-sm font-extrabold text-[#2B4360]">Énoncé</p>
                  <MathExpression>{enonce}</MathExpression>
                </motion.div>
              )}

              <div className="space-y-3">
                {questions.map(([questionKey, questionText], index) => (
                  <motion.div
                    key={questionKey}
                    custom={index + 2}
                    initial="hidden" animate="visible" variants={cardVariants}
                    className="bg-white rounded-3xl border-2 border-slate-200 p-4 sm:p-5 flex items-start gap-4"
                  >
                    <span className="shrink-0 flex items-center justify-center w-11 h-11 rounded-2xl bg-[#2B4360]/[0.07] text-[#2B4360] font-black text-lg">
                      {questionKey.replace('Q', '')}
                    </span>
                    <div className="flex-1 text-base text-slate-700 leading-relaxed min-w-0 pt-2">
                      <MathExpression>{questionText}</MathExpression>
                    </div>
                  </motion.div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* ---- Panneau de soumission ---- */}
      <AnimatePresence>
        {isWidgetOpen && (
          <UploadPanel
            key="upload-panel"
            uploadedImage={uploadedImage}
            isSubmitting={isSubmitting}
            isExerciseCompleted={isExerciseCompleted}
            fileError={fileError}
            locked={isLocked}
            onClose={closeWidget}
            onCamera={() => handleInteraction(() => setShowCustomCamera(true))}
            onGallery={() => handleInteraction(() => fileInputRef.current?.click())}
            onRemove={removeImage}
            onSubmit={handleSubmit}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showPremiumModal && (
          <PremiumModal key="premium" onClose={closePremium} onCheckout={handleCheckout} isRedirecting={isRedirectingToStripe} />
        )}
      </AnimatePresence>

      {/* ---- Action principale, toujours à portée de pouce ---- */}
      <AnimatePresence>
        {widgetsVisible && (
          <motion.div
            key="cta"
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.28, ease: EASE }}
            className="pointer-events-none fixed inset-x-0 bottom-0 z-30 px-4 pt-10 bg-gradient-to-t from-[#F4F7FB] via-[#F4F7FB]/90 to-transparent"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            <div className="max-w-3xl mx-auto">
              <button
                type="button"
                onClick={() => { buzz(); handleInteraction(() => setIsWidgetOpen(true)); }}
                disabled={isExerciseCompleted}
                className={`pointer-events-auto ${BTN_BASE} ${ctaClass} w-full min-h-[60px] text-base`}
              >
                {ctaLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SmartExContent = (props) => (
  <MotionConfig reducedMotion="user">
    <SmartExContentInner {...props} />
  </MotionConfig>
);

export default SmartExContent;